
import { api } from "@/api/api";
import {
  ApiGetMemberOrganizationDto,
  ApiGetOrganizationFeatureSettingDto,
  ApiGetSectorDto,
  ApiUpsertAppFormResponseDto,
} from "@/api/generated/Api";
import BaseCard from "@/components/shared/BaseCard.vue";
import BaseInfoCol from "@/components/shared/BaseInfoCol.vue";
import BaseRow from "@/components/shared/BaseRow.vue";
import { useAutoDeepCloneProp } from "@/fragments/useAutoDeepCloneProp";
import { deepCloneObject } from "@/shared/helpers/deepCloneHelpers";
import { validateBoolean, validateNotEmpty } from "@/shared/helpers/validationHelpers";
import { localeSortByProperty } from "@/shared/helpers/arrayHelpers";
import { defineComponent, onMounted, PropType, ref, Ref, computed, watch } from "@vue/composition-api";
import { debounceHelper } from "@/shared/debounce";
import { log } from "logrocket";

/**
 * Handle selection of Member Organization hierarchy (Main → Regional → Local),
 * optional Division (dropdown or free-text), and Sector.
 *
 * - Clones incoming form model and emits updates via "input"
 * - Resolves correct organizationId based on active dropdowns
 * - Loads organizations, sectors, and feature settings dynamically
 */

interface MemberOrganization {
  id: number;
  name?: string | null;
}

export default defineComponent({
  name: "ApplyCourseUnion",
  components: { BaseCard, BaseInfoCol, BaseRow },
  props: {
    value: {
      // Application form data from parent; cloned locally and updated via emit
      type: Object as PropType<ApiUpsertAppFormResponseDto>,
      required: true,
    },

    // App Form Template feature flags (control which UI sections are shown)
    isMemberOrganization: { type: Boolean, required: true },
    isRegionalOffices: { type: Boolean, default: false },
    isDivisions: { type: Boolean, default: false },
    isSector: { type: Boolean, default: false },

    // Organization-specific feature flags (e.g., supports multi-level org. structure)
    activeFeatureSettings: {
      type: Array as PropType<ApiGetOrganizationFeatureSettingDto[]>,
      // Ideally default should be [], but kept as false for compatibility
      //default: false,
      default: () => [],
    },
  },
  emits: ["input"],
  setup(props, { emit }) {
    // Local appForm model (deep-cloned prop)
    const applicationModel: Ref<ApiUpsertAppFormResponseDto> = useAutoDeepCloneProp<ApiUpsertAppFormResponseDto>(
      props,
      "value"
    );

    // Organizations: full list, parent→children map, and main offices (for top-level selection)
    const memberOrganizations = ref<ApiGetMemberOrganizationDto[]>([]);
    const parentMemberOrganizationMap = ref<Record<number, MemberOrganization[]>>();
    const activeMainOffices = ref<MemberOrganization[]>([]);

    // Currently selected ids across the hierarchy (main → regional → local)
    const selectedMainOfficeId = ref<number>(0);
    const selectedRegionalOfficeId = ref<number>();
    const selectedLocalOfficeId = ref<number>();

    // Division (free-text when no dropdown) and local offices for multi-level setups
    const officeDivisionInput = ref<string>("");
    const memberOrgLocalOffices = ref<ApiGetMemberOrganizationDto[]>([]);
    const loadingLocalOffices = ref<boolean>(false);
    const hasMultiLevel = ref<boolean>(false);

    // Sector options and selection
    const sectors = ref<ApiGetSectorDto[]>([]);
    const selectedSectorId = ref<number | null | undefined>(null);

    // Check feature settings for selected organization; load local offices if multi-level is enabled
    /* const checkFeatureSettings = (id: number) => {
      const memberOrganization = props.activeFeatureSettings.find((x) => x.organizationId === id);

      if (memberOrganization && memberOrganization.hasMultiLevelMemberOrganization) {
        hasMultiLevel.value = true;
        getMemberOrgLocalOffices(memberOrganization.organizationId);
        loadingLocalOffices.value = true;
        return;
      }

      // Reset state if multi-level is not supported
      hasMultiLevel.value = false;
      memberOrgLocalOffices.value = [];
    }; */
    const checkFeatureSettings = (id: number) => {
      const settings = props.activeFeatureSettings ?? [];
      const memberOrganization = settings.find((x) => x.organizationId === id);

      if (memberOrganization?.hasMultiLevelMemberOrganization) {
        hasMultiLevel.value = true;
        getMemberOrgLocalOffices(memberOrganization.organizationId);
        loadingLocalOffices.value = true;
      } else {
        hasMultiLevel.value = false;
        memberOrgLocalOffices.value = [];
      }
    };

    // Fetch local offices for multi-level organizations
    const getMemberOrgLocalOffices = async (id: number) => {
      memberOrgLocalOffices.value = (await api.organization.getMemberOrganizationLocalOfficesAsync(id)).data;

      loadingLocalOffices.value = false;
    };

    // Watch division input; debounce and emit changes
    watch(officeDivisionInput, async () => {
      await debounceHelper.debounce("divisionInput", 100);
      emitChanges();
    });

    // Watch main office selection; reset dependent selections, check features, and emit changes
    watch(selectedMainOfficeId, () => {
      selectedRegionalOfficeId.value = undefined;
      selectedLocalOfficeId.value = undefined;
      checkFeatureSettings(selectedMainOfficeId.value);
      emitChanges();
    });

    // When sector changes, emit new model state.
    watch(selectedSectorId, () => {
      emitChanges();
    });

    // Show regional office dropdown only when the template enables it AND there are regional options
    const showRegionalOffice = computed(
      () => props.isMemberOrganization && props.isRegionalOffices && activeRegionalOffices.value
    );

    // Show divisions (local offices) dropdown only when the template enables it AND local offices list is populated
    const showDivisions = computed(
      () => props.isMemberOrganization && props.isDivisions && memberOrgLocalOffices.value
    );

    // Show sector dropdown only when the template enables it
    const showSector = computed(() => props.isMemberOrganization && props.isSector);

    // Converts 0 (no selection) to undefined; otherwise returns the given ID
    const getSelectedMainOfficeId = (selectedId: number) => (selectedId === 0 ? undefined : selectedId);

    const emitChanges = () => {
      // Use regional office ID if active, otherwise main office ID
      const hasActiveRegionalOffices = props.isRegionalOffices && activeRegionalOffices.value;

      applicationModel.value.memberOrganizationId = hasActiveRegionalOffices
        ? selectedRegionalOfficeId.value
        : getSelectedMainOfficeId(selectedMainOfficeId.value);

      // Override with local office ID if divisions are active and dropdown exists
      const hasActiveDivisions = props.isDivisions && memberOrgLocalOffices.value.length > 0;

      applicationModel.value.memberOrganizationId = hasActiveDivisions
        ? selectedLocalOfficeId.value
        : hasActiveRegionalOffices
        ? selectedRegionalOfficeId.value
        : getSelectedMainOfficeId(selectedMainOfficeId.value);

      // Set sectorId if sectors are enabled
      const hasSectors = props.isSector && sectors.value && sectors.value.length > 0;
      applicationModel.value.sectorId = hasSectors ? selectedSectorId.value : null;

      // If divisions enabled: use free-text if no dropdown, otherwise selected local office name; null if disabled
      applicationModel.value.memberOrganizationDivisionName = props.isDivisions
        ? !hasActiveDivisions
          ? officeDivisionInput.value
          : memberOrgLocalOffices.value?.find((s: any) => s.id === selectedLocalOfficeId.value)?.name
        : null;

      // Emit updated model
      emit("input", deepCloneObject(applicationModel.value));
    };

    const loadMemberOrganizations = async () => {
      // Load organizations, sort by name, and keep only active entries
      memberOrganizations.value = (await api.organization.getMemberOrganizationsAsync()).data
        ?.sort(localeSortByProperty("name"))
        .filter((memberOrganization) => memberOrganization.isActive);

      // Build a lookup map for parentId, return list of its child organizations
      parentMemberOrganizationMap.value = memberOrganizations.value.reduce<Record<number, MemberOrganization[]>>(
        (previous, { parentId, id, name }) => {
          // If no parent or parent is root (1), skip parenting role
          if (parentId === null || parentId === undefined || parentId === 1) {
            return previous;
          }

          const parentOrganization = { id, name };
          const childOrganizations = previous[parentId] ?? [];

          // If parentId exists, append the children organization to that parent's list
          return {
            ...previous,
            [parentId]: [...childOrganizations, parentOrganization],
          };
        },
        {}
      );

      const getFilteredMemberOrganizations = () => {
        if (props.isRegionalOffices) {
          // Include roots and entries with parentId = 1 when regional mode is on
          return memberOrganizations.value.filter(
            (memberOrganization) => !memberOrganization.parentId || memberOrganization.parentId === 1
          );
        }
        // Include only true roots when regional mode is off
        return memberOrganizations.value.filter((memberOrganization) => !memberOrganization.parentId);
      };

      // Build list of main offices from filtered organizations (id + name only)
      activeMainOffices.value = getFilteredMemberOrganizations().reduce<MemberOrganization[]>(
        (previous, { id, name }) => {
          const parentOrganization = { id, name };
          return [...previous, parentOrganization];
        },
        []
      );
    };

    const setSelectedSector = () => {
      // Normalize selection based on loaded list
      const incomingSectorId = applicationModel.value?.sectorId ?? null;
      const sectorExists = incomingSectorId != null && sectors.value?.some((s) => s.id === incomingSectorId);
      // Use null for “not selected”
      selectedSectorId.value = sectorExists ? incomingSectorId : null;
    };

    const loadSectors = async () => {
      // Load sectors
      sectors.value = (await api.sectors.getSectorsAsync()).data;
      setSelectedSector();
    };

    // Regional offices for the selected main office
    const activeRegionalOffices = computed(() => {
      return parentMemberOrganizationMap.value
        ? parentMemberOrganizationMap.value[selectedMainOfficeId.value]
        : undefined;
    });

    onMounted(async () => {
      await loadMemberOrganizations();
      await loadSectors();
    });

    return {
      applicationModel,
      officeDivisionInput,
      showRegionalOffice,
      showDivisions,
      showSector,
      validateNotEmpty,
      emitChanges,
      validateBoolean,
      activeMainOffices,
      sectors,
      selectedSectorId,
      activeRegionalOffices,
      selectedMainOfficeId,
      selectedRegionalOfficeId,
      selectedLocalOfficeId,
      hasMultiLevel,
      memberOrgLocalOffices,
      loadingLocalOffices,
    };
  },
});
